import type { AdoptionDriverKey } from './AdoptionTypes';

/**
 * The adoption scoring algorithm — pure functions over already-aggregated
 * telemetry, so the math is testable and shared by every gateway. All scores are
 * 0–100. Thresholds (volume target, difficulty point values, workweek length,
 * composite weights) are tunable placeholders, centralized here.
 */

/** Interactions/active-user/week that maps to ~63; the saturating curve's knee. */
export const VOLUME_TARGET = 25;
/** A full workweek of daily use = a Frequency of 100. */
export const WORKWEEK_DAYS = 5;

export const DRIVER_WEIGHTS: Record<AdoptionDriverKey, number> = { frequency: 0.3, consistency: 0.25, complexity: 0.25, volume: 0.2 };

/** Low / Medium / High difficulty mapped to 1 / 2 / 3 for the interaction-weighted mean. */
const DIFFICULTY_LEVEL: Record<string, number> = { low: 1, medium: 2, high: 3 };

function clampScore(value: number): number {
    return Math.round(Math.max(0, Math.min(100, value)));
}

/** Frequency = (active days/week ÷ 5) × 100, clamped at a full workweek. */
export function frequencyScore(activeDaysPerWeek: number): number {
    return clampScore((Math.min(activeDaysPerWeek, WORKWEEK_DAYS) / WORKWEEK_DAYS) * 100);
}

/** Volume = 100 × (1 − e^(−avg/target)); a saturating curve over interactions/active-user/week. */
export function volumeScore(interactionsPerUserPerWeek: number): number {
    return clampScore((1 - Math.exp(-Math.max(0, interactionsPerUserPerWeek) / VOLUME_TARGET)) * 100);
}

/** Complexity = (meanLevel − 1) ÷ 2 × 100, with meanLevel the interaction-weighted 1–3 difficulty. */
export function complexityScore(meanLevel: number): number {
    return clampScore(((meanLevel - 1) / 2) * 100);
}

/** Consistency = (weeks active ÷ weeks in window) × 100. */
export function consistencyScore(activeWeeks: number, weeksInWindow: number): number {
    return clampScore((activeWeeks / Math.max(1, weeksInWindow)) * 100);
}

export interface DriverScores {
    frequency: number;
    consistency: number;
    complexity: number;
    volume: number;
}

/** Composite = 0.30·Frequency + 0.25·Consistency + 0.25·Complexity + 0.20·Volume. */
export function compositeScore(d: DriverScores): number {
    return Math.round(
        DRIVER_WEIGHTS.frequency * d.frequency + DRIVER_WEIGHTS.consistency * d.consistency + DRIVER_WEIGHTS.complexity * d.complexity + DRIVER_WEIGHTS.volume * d.volume,
    );
}

/** Maps a difficulty label to its 1–3 level, or null when unrecognized. */
export function difficultyLevel(value: string): number | null {
    return DIFFICULTY_LEVEL[value.trim().toLowerCase()] ?? null;
}

/** Interaction-weighted mean difficulty level (1–3) from per-level turn counts; defaults to 1 (all-low) when empty. */
export function meanDifficultyLevel(counts: { value: string; turns: number }[]): number {
    let weighted = 0;
    let total = 0;
    for (const { value, turns } of counts) {
        const level = difficultyLevel(value);
        if (level === null || turns <= 0) continue;
        weighted += level * turns;
        total += turns;
    }
    return total === 0 ? 1 : weighted / total;
}

/** The mean of the last `n` values (used for the trailing-4-week headline). */
export function trailingMean(series: number[], n: number): number {
    const slice = series.slice(-n);
    return slice.length === 0 ? 0 : slice.reduce((sum, v) => sum + v, 0) / slice.length;
}
